import BankBranchList from "@component/BankBranchList";
import BankList, { BankSelectValue } from "@component/BankList";
import MultipleFileUpload, { UploadedFile } from "@component/Common/MultipleFileUpload";
import bankData from "@constant/bank.json";
import useIntRouteParam from "@hooks/useIntRouteParam";
import useMemberInfo from "@hooks/useMemberInfo";
import { usePermission } from "@hooks/usePermission";
import { fetchVendorDetail, reviewVendorApplication } from "@redux/vendorSlice";
import { formItemValidator } from "@utils/validator";
import { Button, Col, Divider, Form, Input, message, Modal, Radio, Row, Space, Typography } from "antd";
import moment from "moment";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useSelector } from "src/store";
import { TabBottomButtonWrapper, TabContentWrapper } from "src/styles/common";
import styled from "styled-components";
import { permissionMap, VendorApplicationStatus, VendorCategory } from "./constant";
import { VendorDetail, AttachmentPayload, BankAccountItem, CreateVendorPayload } from "./interface";

type HandleReview = {
  (approved: true): Promise<boolean>;
  (approved: false, reason: string): Promise<boolean>;
};

const { Title } = Typography;

const SectionTitle = styled(Title)`
  && {
    font-size: 15px;
    margin-bottom: 16px;
  }
`;
const ReviewStatusDiv = styled.div`
  width: 900px;
  margin-bottom: 18px;
`;
const ReviewStatusCol = styled(Col)<{ header?: boolean }>`
  padding: 8px;
  border: 1px solid #e5e5e5;
  ${({ header }) => header && "background-color: #fafafa;"}
  text-align: center;
`;

const CONTRACT_DIR_PREFIX = "vendor/contract";
const BANK_DIR_PREFIX = "bank_account/attachments";

function toAttachmentPayload(files: UploadedFile[], dirPrefix: string): AttachmentPayload[] {
  return files?.map((file) => ({ dirPrefix, filePath: file.filePath, id: file.id })) || [];
}

export interface VendorInfoFormValues {
  category: number;
  name: string;
  ubn?: string;
  idNumber?: string;
  companyOwner?: string;
  businessAddress: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  fax?: string;
  bankAccount: Omit<BankAccountItem, "attachments" | "bankName"> & {
    attachments: UploadedFile[];
    bankName: BankSelectValue;
  };
  contractMedias: UploadedFile[];
}

interface Props {
  readOnly?: boolean; /** 唯讀模式（檢視） */
  /** 初始資料（編輯 / 檢視時帶入） */
  initialValues?: Partial<VendorDetail>;
  /** 是否顯示送出按鈕 */
  showSubmit?: boolean;
  /** 送出回呼 */
  onFinish?: (values: CreateVendorPayload) => void;
}

const VendorDetails: FC<Props> = ({ readOnly = false, initialValues, showSubmit = true, onFinish }) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [reviewForm] = Form.useForm<{ rejectReason: string }>();
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const { isFetching, vendorDetail } = useSelector((state) => state.vendorSlice);
  const { applicationStatus } = vendorDetail || {};

  const vendorId = useIntRouteParam("vendorId");

  const reviewPermission = permissionMap[applicationStatus || 0];
  const permission = usePermission(reviewPermission);
  const { userId, userName } = useMemberInfo();
  const canSendReview = Boolean(reviewPermission && permission);
  const bankNameField = Form.useWatch<BankSelectValue | undefined>(["bankAccount", "bankName"], form);
  const bankCode = bankNameField?.value;
  const category = Form.useWatch("category", form);
  const isCompany = category === VendorCategory.COMPANY;

  const handleReviewStatus = useMemo(() => {
    if (!vendorDetail) return { manager: "尚未審核", finance: "尚未審核" };
    const { managerReviewer, financeReviewer } = vendorDetail;
    const managerName = managerReviewer?.name ? `/ ${managerReviewer.name}` : "";
    const financeName = financeReviewer?.name ? `/ ${financeReviewer.name}` : "";
    switch (applicationStatus) {
      case VendorApplicationStatus.MANAGER_REJECTED:
        return { manager: `主管駁回 ${managerName}`, finance: "-" };
      case VendorApplicationStatus.PENDING_FILING:
        return { manager: `審核通過 ${managerName}`, finance: "待建檔" };
      case VendorApplicationStatus.FINANCE_REJECTED:
        return { manager: "-", finance: `財務駁回 ${financeName}` };
      case VendorApplicationStatus.PENDING_SYNC:
      case VendorApplicationStatus.COMPLETED:
        return { manager: `審核通過 ${managerName}`, finance: `審核通過 ${financeName}` };
      case VendorApplicationStatus.PENDING_REVIEW:
      default:
        return { manager: "待審核", finance: "-" };
    }
  }, [applicationStatus, vendorDetail]);

  const handleCategoryChange = (value: number) => {
    form.setFieldValue("category", value);
    // 切換類別時清空 ubn / idNumber 欄位與驗證狀態
    form.resetFields(["ubn", "idNumber"]);
  };

  const handleSubmit = (values: VendorInfoFormValues) => {
    if (!onFinish || !userId) return;

    const { bankAccount, contractMedias, ...rest } = values;
    const payload: CreateVendorPayload = {
      ...rest,
      owner: userId,
      bankAccount: {
        ...bankAccount,
        bankName: bankAccount.bankName?.label ?? "",
        attachments: toAttachmentPayload(bankAccount.attachments, BANK_DIR_PREFIX),
      },
      contractMedias: toAttachmentPayload(contractMedias, CONTRACT_DIR_PREFIX),
    };
    onFinish(payload);
  };
  const handleReview: HandleReview = async (approved: boolean, reason?: string) => {
    if (!vendorId) return false;

    try {
      await dispatch(
        reviewVendorApplication({
          id: vendorId,
          isApproved: approved,
          rejectReason: reason,
        }),
      ).unwrap();
      message.success(`審核${approved ? "" : "不"}通過`);
      dispatch(fetchVendorDetail(vendorId));
      return true;
    } catch {
      // 錯誤由 CoreAPI interceptor 統一處理
      return false;
    }
  };

  const handleRejectModalOk = async () => {
    const { rejectReason } = await reviewForm.validateFields();
    const isSuccess = await handleReview(false, rejectReason);
    if (!isSuccess) return;
    setRejectModalVisible(false);
    reviewForm.resetFields();
  };

  useEffect(() => {
    if (vendorDetail) return;
    if (vendorId) dispatch(fetchVendorDetail(vendorId));
  }, [vendorId, vendorDetail, dispatch]);

  // 編輯模式下帶入初始值
  useEffect(() => {
    if (vendorDetail) {
      const { majorContact, bankAccount, ...rest } = vendorDetail;
      form.setFieldsValue({
        ...rest,
        bankAccount: bankAccount && {
          ...bankAccount,
          bankName: {
            value: bankData.find((item) => !item.branchCode && item.bankName === bankAccount.bankName)?.bankCode,
            label: bankAccount.bankName,
          },
        },
        contactName: majorContact?.name,
        contactPhone: majorContact?.phone,
        contactEmail: majorContact?.email,
      });
    }
  }, [vendorDetail, form]);

  return (
    <TabContentWrapper>
      {vendorDetail && (
        <ReviewStatusDiv>
          <SectionTitle level={4}>審核狀態</SectionTitle>
          <Row>
            <ReviewStatusCol span={6} header>
              部門主管
            </ReviewStatusCol>
            <ReviewStatusCol span={6} header>
              財務部門
            </ReviewStatusCol>
          </Row>
          <Row>
            <ReviewStatusCol span={6}>{handleReviewStatus?.manager}</ReviewStatusCol>
            <ReviewStatusCol span={6}>{handleReviewStatus?.finance}</ReviewStatusCol>
          </Row>
          <Row style={{ marginTop: "8px" }}>
            <Col>
              <Typography.Text type="danger">
                {vendorDetail?.rejectReason && `駁回原因 : ${vendorDetail?.rejectReason}`}
              </Typography.Text>
            </Col>
          </Row>
        </ReviewStatusDiv>
      )}
      <Form
        form={form}
        labelAlign="left"
        labelCol={{ span: 4 }}
        wrapperCol={{ span: 16 }}
        layout="horizontal"
        disabled={readOnly}
        onFinish={handleSubmit}
        initialValues={{ category: VendorCategory.COMPANY }}
        onFinishFailed={({ values, errorFields, outOfDate }) => {
          if (outOfDate) return;
          message.error("表單驗證失敗，請檢查欄位提示");
          form.scrollToField(errorFields[0]?.name);
        }}
        style={{ maxWidth: 900 }}
      >
        {/* ── 廠商基本資料 ── */}
        <SectionTitle level={5}>廠商基本資料</SectionTitle>
        {/* 饌元負責人：自動帶入，不開放選擇 */}
        <Form.Item label="饌元負責人">
          <span>{readOnly ? vendorDetail?.owner?.name : userName}</span>
        </Form.Item>
        <Form.Item label="建立日期">
          <span>{moment().format("YYYY-MM-DD")}</span>
        </Form.Item>

        <Form.Item label="廠商類別" name="category" rules={[{ required: true, message: "請選擇廠商類別" }]}>
          <Radio.Group
            onChange={(e) => {
              handleCategoryChange(e.target.value);
            }}
          >
            <Radio value={VendorCategory.COMPANY}>公司戶</Radio>
            <Radio value={VendorCategory.PERSONAL}>個人戶</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="廠商名稱"
          name="name"
          rules={[
            { required: true, message: "請填入廠商名稱" },
            { max: 50, message: "廠商名稱不可超過 50 字元" },
          ]}
        >
          <Input maxLength={50} showCount />
        </Form.Item>

        {isCompany ? (
          <Form.Item
            label="統一編號"
            name="ubn"
            rules={[{ required: true, message: "" }, { validator: formItemValidator.ubn() }]}
          >
            <Input />
          </Form.Item>
        ) : (
          <Form.Item
            label="身份證字號"
            name="idNumber"
            rules={[{ required: true, message: "" }, { validator: formItemValidator.idNumber() }]}
          >
            <Input />
          </Form.Item>
        )}

        {isCompany && (
          <Form.Item label="公司負責人" name="companyOwner" rules={[{ required: true, message: "請填入公司負責人" }]}>
            <Input />
          </Form.Item>
        )}

        <Form.Item label="業務聯絡人" name="contactName" rules={[{ required: true, message: "請填入業務聯絡人" }]}>
          <Input />
        </Form.Item>

        <Form.Item label="聯絡人電話" name="contactPhone" rules={[{ required: true, message: "請填入聯絡人電話" }]}>
          <Input />
        </Form.Item>

        <Form.Item
          label="聯絡人信箱"
          name="contactEmail"
          rules={[
            { required: true, message: "" },
            { type: "email", message: "請填入正確的 Email 格式" },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item label="傳真" name="fax">
          <Input />
        </Form.Item>

        <Form.Item label="公司地址" name="businessAddress" rules={[{ required: true, message: "請填入公司地址" }]}>
          <Input />
        </Form.Item>

        <Form.Item label="合約附件" name="contractMedias">
          <MultipleFileUpload
            dirPrefix={CONTRACT_DIR_PREFIX}
            onChange={(files) => {
              form.setFields([{ name: ["contractMedias"], value: files }]);
            }}
            readOnly={readOnly}
          />
        </Form.Item>

        <Divider />

        {/* ── 銀行匯款資訊 ── */}
        <SectionTitle level={5}>銀行匯款資訊</SectionTitle>

        <Form.Item
          label="帳戶名稱"
          name={["bankAccount", "accountName"]}
          rules={[{ required: true, message: "請填入帳戶名稱" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="帳戶號碼"
          name={["bankAccount", "accountNumber"]}
          rules={[
            { required: true, message: "" },
            { pattern: /^\d+$/, message: "請填入正確的帳戶號碼，僅允許數字" },
          ]}
        >
          <Input
            onKeyPress={(e) => {
              // 阻擋非數字字元輸入
              if (!/\d/.test(e.key)) e.preventDefault();
            }}
          />
        </Form.Item>

        <Form.Item
          label="銀行名稱"
          name={["bankAccount", "bankName"]}
          rules={[{ required: true, message: "請選擇銀行" }]}
        >
          <BankList
            labelInValue
            onBankCodeChange={() => form.setFieldValue(["bankAccount", "branchName"], undefined)}
          />
        </Form.Item>

        <Form.Item
          label="分行名稱"
          name={["bankAccount", "branchName"]}
          rules={[{ required: true, message: "請選擇分行" }]}
        >
          <BankBranchList bankCode={bankCode} />
        </Form.Item>

        <Form.Item
          label="匯款通知信箱"
          name={["bankAccount", "remitEmail"]}
          rules={[
            { required: true, message: "" },
            { type: "email", message: "請填入正確的 Email 格式" },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="銀行帳戶附件"
          name={["bankAccount", "attachments"]}
          rules={[
            { required: true, message: "請上傳銀行帳戶影本" },
            {
              validator: (_, value) => {
                // 銀行附件數量驗證:公司戶1,個人戶2
                const categoryType = form.getFieldValue("category");
                const limit = categoryType === VendorCategory.COMPANY ? 1 : 2;
                if (value && value.length > limit) {
                  return Promise.reject(new Error(`僅能上傳 ${limit} 個銀行附件`));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <MultipleFileUpload
            dirPrefix={BANK_DIR_PREFIX}
            onChange={(files) => {
              form.setFields([{ name: ["bankAccount", "attachments"], value: files }]);
            }}
            readOnly={readOnly}
            showHint={showSubmit}
            hint={isCompany ? "請上傳銀行帳戶影本（需蓋發票章）" : "請上傳身分證影本及銀行帳戶影本"}
          />
        </Form.Item>
        <TabBottomButtonWrapper>
          <Space>
            {showSubmit && !readOnly && (
              <Button type="primary" htmlType="submit" loading={isFetching}>
                提交
              </Button>
            )}
            {canSendReview && (
              <>
                <Button type="primary" loading={isFetching} disabled={!permission} onClick={() => handleReview(true)}>
                  審核通過
                </Button>
                <Button
                  type="primary"
                  loading={isFetching}
                  disabled={!permission}
                  onClick={() => setRejectModalVisible(true)}
                >
                  審核不通過
                </Button>
              </>
            )}
          </Space>
        </TabBottomButtonWrapper>
      </Form>
      <Modal
        title="審核不通過"
        open={rejectModalVisible}
        confirmLoading={isFetching}
        okText="確認"
        cancelText="取消"
        onOk={handleRejectModalOk}
        onCancel={() => {
          setRejectModalVisible(false);
          reviewForm.resetFields();
        }}
      >
        <Form form={reviewForm} layout="vertical">
          <Form.Item
            label="不通過原因"
            name="rejectReason"
            rules={[
              { required: true, message: "請填寫不通過原因" },
              { max: 50, message: "不通過原因不可超過 50 字元" },
            ]}
          >
            <Input.TextArea rows={4} maxLength={50} showCount placeholder="請填寫不通過原因" />
          </Form.Item>
        </Form>
      </Modal>
    </TabContentWrapper>
  );
};

export default VendorDetails;
